import React, { useState, useRef, useEffect, useMemo } from "react";
import ReactECharts from "echarts-for-react";
import {
  ArrowDownTrayIcon,
  ArrowPathIcon,
  ChartBarIcon,
  ChevronDownIcon,
  SignalIcon,
} from "@heroicons/react/24/outline";
import { useWeatherData } from "../../queries/admin";
import { useDeviceStore } from "../../store/useDeviceStore";
import { useGetDeviceParameters } from "../../queries/parameters";
import { useDashboard } from "../../pages/dashboard/hooks/useDashboard";
import { filterWeatherData, TimeGap, TimePeriod } from "../../utils/TimeConstants";
import { EChartOption } from "echarts";
import { IWeatherData } from "../../types/device";
import DaysSelector from "../../components/DaysSelector";

interface GraphWidgetProps {
  isEditMode?: boolean;
}

const timeRanges: { id: string; name: TimePeriod }[] = [
  { id: "1", name: "1 Day" },
  { id: "2", name: "7 Days" },
  { id: "3", name: "30 Days" },
  { id: "4", name: "60 Days" },
  { id: "5", name: "90 Days" },
];

const timeGaps: { id: string; name: TimeGap }[] = [
  { id: "1", name: "1 hour" },
  { id: "2", name: "2 hour" },
  { id: "3", name: "6 hour" },
  { id: "4", name: "12 hour" },
  { id: "5", name: "24 hour" },
];

function getParameterColor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  const hue = Math.abs(hash) % 360;
  return `hsl(${hue}, 65%, 55%)`;
}

const GraphWidget: React.FC<GraphWidgetProps> = ({ isEditMode = false }) => {
  const { selectedDeviceId: deviceId, selectedDeviceIds } = useDeviceStore();
  const isMultiDevice = selectedDeviceIds.length > 1;
  const {
    selectedParams,
    selectedDay,
    selectedTimeGap,
    setSelectedDay,
    setSelectedParams,
    setAllSelectedParams,
    setSelectedTimeGap,
  } = useDashboard();

  const [timeDropdownOpen, setTimeDropdownOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  // Series hidden via the custom legend. Keyed by series name; toggling only
  // changes chart visibility (via legend.selected) and never refetches data.
  const [hiddenSeries, setHiddenSeries] = useState<Record<string, boolean>>({});
  const timeDropdownRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<ReactECharts>(null);
  const defaultedDeviceRef = useRef<string | null>(null);

  const metrics = selectedParams.join(",");

  const { data: _weatherData, refetch, isFetching } = useWeatherData(deviceId, metrics, selectedDay,selectedTimeGap);
  const { data: _deviceParameters } = useGetDeviceParameters(deviceId, !!deviceId);
  const deviceParameters = _deviceParameters || [];
  const validDeviceParameters = deviceParameters.filter(
    (dp) => dp.dataType === "INTEGER" || dp.dataType === "FLOAT"
  );
  const rawWeatherData = _weatherData || [];

  const filteredData = useMemo(() => {
    if (rawWeatherData && rawWeatherData.length > 0) {
      return filterWeatherData(rawWeatherData, selectedDay, selectedTimeGap);
    } else {
      return [];
    }
  }, [rawWeatherData, selectedDay, selectedTimeGap]);

  const timeLabels = filteredData.map((d) => d.timestamp);
  const weatherData = filteredData.map(
    ({ id, timestamp, deviceId, createdAt, updatedAt, ...rest }) => rest
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
      if (timeDropdownRef.current && !timeDropdownRef.current.contains(event.target as Node)) {
        setTimeDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if(deviceId){
      refetch();
    }
  }, [deviceId, selectedParams]);

  // Select every available parameter by default the first time a device's graph
  // is shown with no saved selection, so the chart isn't empty on first view.
  // Guarded per-device so it runs once and never re-fills a user's deliberate
  // deselection within the same session.
  useEffect(() => {
    if (!deviceId || validDeviceParameters.length === 0) return;
    if (defaultedDeviceRef.current === deviceId) return;
    defaultedDeviceRef.current = deviceId;
    if (selectedParams.length === 0) {
      setAllSelectedParams(
        validDeviceParameters.map((param) => param.identifier),
        deviceId,
      );
    }
  }, [deviceId, validDeviceParameters, selectedParams]);

  // Graph view doesn't support multi-device aggregation — render a disabled state.
  // This early return must come AFTER all hooks to satisfy React's rules of hooks.
  if (isMultiDevice) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
        <div className="bg-gradient-to-r from-slate-50 to-gray-50 border-b border-gray-200 px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-50 rounded-lg">
              <ChartBarIcon className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Multi-Parameter Graph</h3>
              <p className="text-sm text-gray-500">Historical trend visualization</p>
            </div>
          </div>
        </div>
        <div className="flex flex-col items-center justify-center px-6 py-16 text-center">
          <div className="p-3 bg-gray-50 rounded-full mb-4">
            <ChartBarIcon className="h-8 w-8 text-gray-400" />
          </div>
          <h4 className="text-base font-semibold text-gray-800 mb-1">
            Graph view is disabled for multi-device selection
          </h4>
          <p className="text-sm text-gray-500 max-w-md">
            Historical graphs are only available when a single device is selected. Deselect extra
            devices to view trends for one device at a time.
          </p>
          <div className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium bg-indigo-50 text-indigo-700 border border-indigo-200">
            <SignalIcon className="w-3 h-3" />
            <span>{selectedDeviceIds.length} devices selected</span>
          </div>
        </div>
      </div>
    );
  }

  const isDarkMode = document.documentElement.classList.contains("dark");

  const handleDownloadSnapshot = () => {
    const echartsInstance = chartRef.current?.getEchartsInstance();
    if (!echartsInstance) return;

    const dataUrl = echartsInstance.getDataURL({
      type: "png",
      pixelRatio: 2,
      backgroundColor: isDarkMode ? "#1F2937" : "#FFFFFF",
    });

    const link = document.createElement("a");
    link.href = dataUrl;
    link.download = `graph-${deviceId || "snapshot"}-${selectedDay}-${selectedTimeGap}.png`;
    link.click();
  };

  const series = selectedParams.map((parameter) => {
    const data = weatherData.map((item) =>
      item[parameter] && typeof item[parameter] === "number" ? item[parameter] : 0
    );
    const param = validDeviceParameters.find((dp) => dp.identifier === parameter);
    return {
      name: param?.name || parameter,
      type: "line",
      smooth: true,
      data,
      lineStyle: {
        color: getParameterColor(param?.name || ""),
        width: 2,
      },
      itemStyle: { color: getParameterColor(param?.name || "") },
    };
  });

  // Custom legend rendered as centered, wrapping HTML below. ECharts' built-in
  // legend centers the block but left-aligns each wrapped row, so short trailing
  // rows hug the left edge; flexbox `justify-center` centers every row instead.
  const legendItems = selectedParams.map((parameter) => {
    const param = validDeviceParameters.find((dp) => dp.identifier === parameter);
    return {
      key: parameter,
      name: param?.name || parameter,
      color: getParameterColor(param?.name || ""),
    };
  });

  const toggleSeries = (name: string) =>
    setHiddenSeries((prev) => ({ ...prev, [name]: !prev[name] }));

  const chartOptions: EChartOption = {
    backgroundColor: isDarkMode ? "#1F2937" : "transparent",
    tooltip: {
      trigger: "axis",
      backgroundColor: isDarkMode ? "rgba(0,0,0,0.8)" : "rgba(255,255,255,0.95)",
      borderColor: isDarkMode ? "#374151" : "#E5E7EB",
      textStyle: {
        color: isDarkMode ? "#F9FAFB" : "#111827",
      },
    },
    // Built-in legend is hidden in favour of the centered HTML legend below;
    // `selected` still drives which series are visible when items are toggled.
    legend: {
      show: false,
      selected: legendItems.reduce<Record<string, boolean>>((acc, item) => {
        acc[item.name] = !hiddenSeries[item.name];
        return acc;
      }, {}),
    },
    grid: {
      top: 24,
      bottom: 50,
      left: 50,
      right: 60,
      containLabel: true,
    },
    xAxis: {
      type: "category",
      data: timeLabels,
      axisLine: {
        lineStyle: { color: isDarkMode ? "#4B5563" : "#D1D5DB" },
      },
      axisLabel: {
        color: isDarkMode ? "#9CA3AF" : "#6B7280",
        fontSize: 11,
      },
      axisTick: {
        lineStyle: { color: "#D1D5DB" },
      },
      splitLine: {
        show: true,
        lineStyle: {
          color: isDarkMode ? "#374151" : "#F3F4F6",
          type: "dashed",
        },
      },
    },
    yAxis: {},
    series,
  };

  const selectedCount = selectedParams.length;
  const selectedLabels = validDeviceParameters
    .filter((param) => selectedParams.includes(param.identifier))
    .map((param) => param.name);

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
      {/* Graph Header - Controls */}
      <div className="bg-gradient-to-r from-slate-50 to-gray-50 border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="relative w-28 sm:w-32 font-semibold" ref={timeDropdownRef}>
              <button
                className="h-9 cursor-pointer rounded-lg bg-white dark:bg-[#111B21] pl-2 sm:pl-3 pr-8 sm:pr-10 text-left text-gray-900 dark:text-gray-200 border border-gray-200 dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-primary text-sm w-28 sm:w-32 font-semibold truncate flex items-center"
                onClick={() => setTimeDropdownOpen(!timeDropdownOpen)}
              >
                <span className="block truncate font-semibold">{selectedTimeGap}</span>
                <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-1 sm:pr-2">
                  <ChevronDownIcon className="h-4 w-4 text-gray-400" />
                </span>
              </button>

              {timeDropdownOpen && (
                <div className="absolute z-50 mt-1 w-full bg-white dark:bg-gray-800 rounded-lg shadow-sm max-h-60 overflow-auto ring-1 ring-black ring-opacity-5 focus:outline-none">
                  <ul className="py-1">
                    {timeGaps.map((gap) => (
                      <li
                        key={gap.id}
                        className={`cursor-pointer select-none relative px-4 py-2 text-xs sm:text-sm ${
                          selectedTimeGap === gap.name
                            ? "bg-primary/10 text-primary dark:bg-primary/20 dark:text-primary"
                            : "text-gray-900 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700"
                        }`}
                        onClick={() => {
                          setSelectedTimeGap(gap.name, deviceId);
                          setTimeDropdownOpen(false);
                        }}
                      >
                        {gap.name}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            <DaysSelector
              options={timeRanges.map((range) => ({
                value: range.name,
                label: `${parseInt(range.name)}d`,
              }))}
              selected={selectedDay}
              onSelect={(name) => setSelectedDay(name, deviceId)}
              className="overflow-x-auto sm:overflow-visible flex-shrink-0"
            />
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => refetch()}
              disabled={isFetching || !deviceId}
              title="Refresh data"
              className="flex items-center justify-center h-9 w-9 bg-white border border-gray-200 rounded-lg hover:bg-blue-50 hover:border-blue-300 transition-all duration-200 text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:border-gray-200"
            >
              <ArrowPathIcon className={`h-4 w-4 ${isFetching ? "animate-spin" : ""}`} />
            </button>
            <button
              type="button"
              onClick={handleDownloadSnapshot}
              disabled={selectedCount === 0}
              title="Download snapshot"
              className="flex items-center justify-center h-9 w-9 bg-white border border-gray-200 rounded-lg hover:bg-blue-50 hover:border-blue-300 transition-all duration-200 text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:border-gray-200"
            >
              <ArrowDownTrayIcon className="h-4 w-4" />
            </button>
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="flex items-center gap-4 px-3 sm:px-4 h-9 bg-white border border-gray-200 rounded-lg hover:bg-blue-50 hover:border-blue-300 transition-all duration-200 text-sm font-medium text-gray-700"
              >
                <span>
                  {selectedCount === 0
                    ? "Select Parameters"
                    : selectedCount === 1
                    ? selectedLabels[0]
                    : `${selectedCount} Parameters`}
                </span>
                <ChevronDownIcon
                  className={`h-4 w-4 transition-transform duration-200 ${
                    isDropdownOpen ? "rotate-180" : ""
                  }`}
                />
              </button>

              {isDropdownOpen && (
              <div className="absolute right-0 mt-2 w-72 bg-white border border-gray-200 rounded-xl shadow-lg z-20 backdrop-blur-sm">
                <div className="p-4">
                  <div className="flex items-center justify-between mb-4">
                    <div className="text-sm font-semibold text-gray-900">
                      Select Parameters
                    </div>
                    <div className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                      {selectedCount} selected
                    </div>
                  </div>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {validDeviceParameters.map((param) => (
                      <label
                        key={param.identifier}
                        className="flex items-center gap-3 px-3 py-2.5 hover:bg-blue-50/70 rounded-lg cursor-pointer transition-all duration-200 group"
                      >
                        <input
                          type="checkbox"
                          checked={selectedParams.includes(param.identifier)}
                          onChange={() => setSelectedParams(param.identifier, deviceId)}
                          className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded transition-all duration-200"
                        />
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <div
                            className="w-3 h-3 rounded-full shadow-sm ring-1 ring-white/30"
                            style={{
                              backgroundColor: getParameterColor(param.name),
                            }}
                          />
                          <div className="flex-1 min-w-0">
                            <span className="text-sm font-medium text-gray-900 truncate block">
                              {param.name}
                            </span>
                          </div>
                          <span className="text-xs text-gray-500 font-medium bg-gray-50 px-2 py-1 rounded-md">
                            {param.unit}
                          </span>
                        </div>
                      </label>
                    ))}
                  </div>
                  {validDeviceParameters.length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                      <div className="text-gray-400 mb-2">
                        <ChartBarIcon className="h-8 w-8 mx-auto" />
                      </div>
                      <p className="text-sm">No parameters available</p>
                    </div>
                  )}
                </div>
              </div>
            )}
            </div>
          </div>
        </div>
      </div>

      {/* Chart Container */}
      <div className="bg-white p-6">
        {/* Centered, wrapping legend. Click an item to show/hide its series. */}
        {legendItems.length > 0 && (
          <div className="mb-4 flex flex-wrap items-center justify-center gap-x-4 gap-y-2">
            {legendItems.map((item) => {
              const hidden = !!hiddenSeries[item.name];
              return (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => toggleSeries(item.name)}
                  title={hidden ? `Show ${item.name}` : `Hide ${item.name}`}
                  className="flex items-center gap-1.5 text-xs font-medium focus:outline-none"
                >
                  <span
                    className="inline-block h-3 w-3 rounded-sm"
                    style={{ backgroundColor: hidden ? "#D1D5DB" : item.color }}
                  />
                  <span
                    className={
                      hidden
                        ? "text-gray-400 line-through dark:text-gray-500"
                        : "text-gray-600 dark:text-gray-300"
                    }
                  >
                    {item.name}
                  </span>
                </button>
              );
            })}
          </div>
        )}
        <ReactECharts
          ref={chartRef}
          option={chartOptions}
          notMerge={true}
          style={{ height: "450px", width: "100%" }}
          opts={{ renderer: "canvas" }}
        />
      </div>

      {/* Graph Footer */}
      <div className="bg-gray-50/50 border-t border-gray-200 px-6 py-4 shadow-sm">
        <div className="flex items-center justify-between text-sm text-gray-900">
          <span className="font-medium">
            Showing {selectedCount} Parameter{selectedCount !== 1 ? "s" : ""}
          </span>
          <span className="text-xs text-gray-900">
            Last {selectedDay} &bull; {selectedTimeGap} interval
          </span>
        </div>
      </div>

      {/* Edit Mode Notice */}
      {isEditMode && (
        <div className="bg-blue-50 border-t border-blue-200 px-6 py-3">
          <p className="text-sm text-blue-800">
            🎨 Edit Mode: Use the dropdown in the top-right corner of the chart to select parameters
          </p>
        </div>
      )}
    </div>
  );
};

export default GraphWidget;
